/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ROUTES from 'modules/common/constants/route';
import useIsAuthenticated from 'modules/common/auth/hooks/use-is-authenticated';
import { useDispatch } from 'react-redux';
import { authActions } from 'modules/common/auth/slice';
/**
 * Check is authenticated or not and if not authenticate then route to login
 * @returns
 */
const useAuthorization = () => {
  const { isAuthenticated, checkTokenActivity, isTokenRevoked } = useIsAuthenticated();
  //
  const navigate = useNavigate();
  const dispatch = useDispatch();
  //
  useEffect(() => {
    // TokenRevokedPopup owns the redirect so the client-required Session
    // Expired dialog stays visible on top of the dashboard.
    if (isTokenRevoked) return undefined;
    if (!isAuthenticated()) {
      dispatch(authActions.removeAuth());
      navigate(ROUTES.LOGIN);
      return undefined;
    }
    const controller = new AbortController();
    checkTokenActivity(controller.signal).then((active) => {
      if (!active && !controller.signal.aborted) {
        // Raising the flag shows TokenRevokedPopup; isAuthenticated() is false
        // so sign-in-view signs out after OK, and signInActions.signIn resets it.
        dispatch(authActions.setTokenRevocationError(true));
      }
    });
    return () => controller.abort();
  }, [isAuthenticated, checkTokenActivity, isTokenRevoked]);
  //
  return { isAuthenticated, isTokenRevoked };
};
//
export default useAuthorization;
